import { CHECK_ICON, COMMITMENT_CARDS, METHOD_CARDS, TESTIMONIAL_CARDS } from './panelsData'
import { useGeneralSettings } from '../../../hooks/useGeneralSettings'
import { appUrl } from '@astro/config'

const val = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '')

/** tel: wants digits; bare 10-digit Indian numbers get the 91 prefix. */
const telDigits = (raw) => {
  const digits = raw.replace(/\D/g, '')
  return digits.length === 10 ? `91${digits}` : digits
}

function FeatureIcon({ card }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {card.circle && <circle cx={card.circle.cx} cy={card.circle.cy} r={card.circle.r} />}
      {card.rect && <rect width={card.rect.width} height={card.rect.height} x={card.rect.x} y={card.rect.y} rx={card.rect.rx} ry={card.rect.ry} />}
      {card.polygon && <polygon points={card.polygon} />}
      {card.paths?.map((d, i) => (
        <path key={i} d={d} />
      ))}
      {card.lines?.map(([x1, y1, x2, y2], i) => (
        <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />
      ))}
    </svg>
  )
}

function FeatureCard({ card }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/4 px-3.5 py-2.5 text-left backdrop-blur-sm transition-colors hover:border-goldPrimary/40 hover:bg-white/6">
      <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-goldPrimary/10 text-goldPrimary ring-1 ring-goldPrimary/20">
        <FeatureIcon card={card} />
      </span>
      <span className="min-w-0">
        <span className="block text-[12px] font-semibold uppercase tracking-wide text-[#FBF3E4]">{card.title}</span>
        <span className="block text-[11px] leading-snug text-white/50 normal-case">{card.desc}</span>
      </span>
    </div>
  )
}

function TestimonialCard({ card }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/4 px-3.5 py-2.5 text-left backdrop-blur-sm transition-colors hover:border-goldPrimary/40 hover:bg-white/6">
      <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-goldPrimary/10 text-goldPrimary ring-1 ring-goldPrimary/20 text-base leading-none">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d={CHECK_ICON} />
        </svg>
      </span>
      <span className="min-w-0">
        <span className="block text-[12px] font-semibold uppercase tracking-wide text-[#FBF3E4]">{card.name}</span>
        <span className="block text-[11px] leading-snug text-white/50 normal-case italic">"{card.quote}"</span>
      </span>
    </div>
  )
}

export default function WhyChoosePanel({ index, active, current }) {
  const { settings } = useGeneralSettings()
  const phone = val(settings.support_phone) || val(settings.phone);

  const style =
    index === 0
      ? { transition: 'opacity .55s ease, transform .55s ease', opacity: active ? 1 : 0, transform: active ? 'none' : index < current ? 'translateY(-16px)' : 'translateY(16px)' }
      : {
          position: 'absolute',
          inset: 0,
          transition: 'opacity .55s ease, transform .55s ease',
          opacity: active ? 1 : 0,
          transform: active ? 'none' : index < current ? 'translateY(-16px)' : 'translateY(16px)',
          pointerEvents: active ? 'auto' : 'none',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }

  return (
    <div id={`wcu-slide-${index}`} data-wcu-slide={index} style={style}>
      {index === 0 && (
        <div className="space-y-5">
          <PanelLabel num="01" label="Who We Are" />
          <h2 className="font-royal text-2xl leading-tight text-[#FBF3E4] md:text-4xl lg:text-5xl">
            Thousands Have
            <br />
            <span className="bg-linear-to-r from-goldPrimary via-amber-200 to-goldAccent bg-clip-text text-transparent">Found Their Truth</span>
          </h2>
          <p className="mx-auto max-w-xl text-sm leading-relaxed text-white/65 md:text-[15px] lg:mx-0 normal-case">
            Every reading tells a story of transformation. Real seekers, real breakthroughs — guided by 15+ years of authentic Vedic wisdom.
          </p>
          <div className="grid grid-cols-1 gap-2.5 pt-1 sm:grid-cols-2">
            {TESTIMONIAL_CARDS.map((card) => (
              <TestimonialCard card={card} key={card.name} />
            ))}
          </div>
        </div>
      )}
      {index === 1 && (
        <div className="space-y-5">
          <PanelLabel num="02" label="Our Mission" />
          <h2 className="font-royal text-2xl leading-tight text-[#FBF3E4] md:text-4xl lg:text-5xl">
            Ancient Precision,
            <br />
            <span className="bg-linear-to-r from-goldPrimary via-amber-200 to-goldAccent bg-clip-text text-transparent">Modern Clarity</span>
          </h2>
          <p className="mx-auto max-w-xl text-sm leading-relaxed text-white/65 md:text-[15px] lg:mx-0 normal-case">
            Each reading follows the classical Parashara system — birth chart, planetary dashas, transit analysis — refined
            through 15+ years of direct practice.
          </p>
          <div className="grid grid-cols-1 gap-2.5 pt-1 sm:grid-cols-2">
            {METHOD_CARDS.map((card) => (
              <FeatureCard card={card} key={card.title} />
            ))}
          </div>
        </div>
      )}
      {index === 2 && (
        <div className="space-y-5">
          <PanelLabel num="03" label="Our Vision" />
          <h2 className="font-royal text-2xl leading-tight text-[#FBF3E4] md:text-4xl lg:text-5xl">
            An Unwavering
            <br />
            <span className="bg-linear-to-r from-goldPrimary via-amber-200 to-goldAccent bg-clip-text text-transparent">Sacred Promise</span>
          </h2>
          <p className="mx-auto max-w-xl text-sm leading-relaxed text-white/65 md:text-[15px] lg:mx-0 normal-case">
            Your trust is sacred. We hold every consultation in the strictest confidence and deliver only honest, authentic
            guidance — never what is merely comfortable.
          </p>
          <div className="grid grid-cols-1 gap-2.5 pt-1 sm:grid-cols-2">
            {COMMITMENT_CARDS.map((card) => (
              <FeatureCard card={card} key={card.title} />
            ))}
          </div>
        </div>
      )}
      {index === 3 && (
        <div className="space-y-6">
          <PanelLabel num="04" label="Begin Your Journey" />
          <h2 className="font-royal text-2xl leading-tight text-[#FBF3E4] md:text-4xl lg:text-5xl">
            Your Stars Are
            <br />
            <span className="bg-linear-to-r from-goldPrimary via-amber-200 to-goldAccent bg-clip-text text-transparent">Aligned For This</span>
          </h2>
          <p className="mx-auto max-w-xl text-sm leading-relaxed text-white/65 md:text-[15px] lg:mx-0 normal-case">
            Every moment of hesitation is a planetary cycle passing unfulfilled. The chart is drawn — the question is whether
            you will read it.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2 lg:justify-start">
            <a
              href={appUrl("education", "classes")}
              className="shine-btn w-full sm:w-auto bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.22em] px-8 py-3.5 rounded-full shadow-xl shadow-goldPrimary/20 transition-all hover:scale-105 flex items-center justify-center gap-2.5"
            >
              Explore All Courses
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </a>
            <a
              href={`tel:+${telDigits(phone)}`}
              className="w-full sm:w-auto bg-white/5 border border-white/10 hover:bg-white/10 text-white font-semibold text-[10px] text-center uppercase tracking-[0.22em] px-8 py-3.5 rounded-full transition-all"
            >
              Call Directly
            </a>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-5 pt-3 lg:justify-start">
            <div className="flex items-center gap-2">
              <span className="text-goldPrimary text-xs">★★★★★</span>
              <span className="text-[10px] text-white/40 uppercase tracking-[.25em]">4.9 / 5.0</span>
            </div>
            <div className="h-3 w-px bg-white/15" />
            <span className="text-[10px] text-white/40 uppercase tracking-[.25em]">10k+ Readings</span>
            <div className="h-3 w-px bg-white/15" />
            <span className="text-[10px] text-white/40 uppercase tracking-[.25em]">10+ Countries</span>
          </div>
        </div>
      )}
    </div>
  )
}

function PanelLabel({ num, label }) {
  return (
    <div className="flex items-center justify-center gap-3 lg:justify-start">
      <span className="font-mono text-sm text-goldPrimary/70">{num}</span>
      <span className="h-px w-8 bg-goldPrimary/50" />
      <span className="text-[10px] uppercase tracking-[0.4em] text-goldPrimary">{label}</span>
    </div>
  )
}
